/**
 * Shared Lureo map pin marker: SVG generation and Google Maps icon descriptor.
 * Used by project-map.tsx and locationAnchorModal.tsx.
 */

import { themeColour, themeFont } from '@/modules/theme';

// The pin label's font where the theme sets none
const LABEL_FONT = 'system-ui, sans-serif';

// The pin's default fill: the soft call to action's mint, its old hex in Light and Dark
const DEFAULT_COLOR_TOKEN = 'lureo-cta-soft-to';

// Rendered size (px) and anchor point (pin tip)
export const LUREO_PIN_DEFAULT_WIDTH = 80;
export const LUREO_PIN_DEFAULT_HEIGHT = 120;
export const LUREO_PIN_ANCHOR_X = 40;
export const LUREO_PIN_ANCHOR_Y = 48;

// SVG viewBox and layout (used by project-map for story labels and map snapshot service)
export const SVG_VIEWBOX_WIDTH = 400;
export const SVG_VIEWBOX_HEIGHT = 600;
export const SVG_PIN_CENTER_X = 200;
export const SVG_TEXT_Y_BASE = 280;
export const SVG_TEXT_OFFSET = 64;
export const SVG_PILL_HEIGHT = 114;
export const SVG_PILL_RADIUS = 57;

export interface LureoPinOptions {
  /** Fill color (default: the theme's soft call-to-action mint) */
  color?: string;
  /** Optional label below pin (e.g. "Story 2") */
  label?: string;
  /** Rendered width in px (default 80) */
  width?: number;
  /** Rendered height in px (default 120) */
  height?: number;
}

/**
 * Returns a data:image/svg+xml URL string for the Lureo teardrop pin.
 */
export function createLureoPinSvgUrl(options: LureoPinOptions = {}): string {
  const svg = createLureoPinSvg(options);
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}

/**
 * Returns the raw SVG string for the pin (for embedding or encoding).
 */
export function createLureoPinSvg(options: LureoPinOptions = {}): string {
  const color = options.color ?? themeColour(DEFAULT_COLOR_TOKEN);
  const label = options.label;
  const width = options.width ?? LUREO_PIN_DEFAULT_WIDTH;
  const height = options.height ?? LUREO_PIN_DEFAULT_HEIGHT;
  const cx = SVG_PIN_CENTER_X;

  const fontSize = label && label.length > 7 ? 48 : 64;
  const labelWidth = label ? label.length * fontSize * 0.6 + 60 : 0;
  const labelX = cx - labelWidth / 2;
  const labelY = SVG_TEXT_Y_BASE - SVG_TEXT_OFFSET - 15 + SVG_TEXT_OFFSET;
  const textY = SVG_TEXT_Y_BASE + SVG_TEXT_OFFSET;

  const labelSvg = label
    ? `
    <!-- Label shade -->
    <rect x="${labelX + 2}" y="${labelY + 4}" width="${labelWidth}" height="${SVG_PILL_HEIGHT}" rx="${SVG_PILL_RADIUS}" ry="${SVG_PILL_RADIUS}" fill="${themeColour('lureo-emphasis', 0.2)}"/>
    <!-- Text background -->
    <rect x="${labelX}" y="${labelY}" width="${labelWidth}" height="${SVG_PILL_HEIGHT}" rx="${SVG_PILL_RADIUS}" ry="${SVG_PILL_RADIUS}" fill="${themeColour('lureo-surface', 0.95)}" stroke="${themeColour('lureo-emphasis', 0.1)}" stroke-width="1"/>
    <text x="${cx}" y="${textY}" text-anchor="middle" fill="${themeColour('lureo-ink-strong')}" font-family="${escapeXml(themeFont(LABEL_FONT))}" font-size="${fontSize}" font-weight="500">${escapeXml(label)}</text>
  `
    : '';

  return `<svg width="${width}" height="${height}" viewBox="0 0 ${SVG_VIEWBOX_WIDTH} ${SVG_VIEWBOX_HEIGHT}" fill="none" xmlns="http://www.w3.org/2000/svg">
  <!-- Pin shade -->
  <path d="M204,24a88.1,88.1,0,0,0-88,88c0,75.3,80,132.17,83.41,134.55a8,8,0,0,0,9.18,0C212,244.17,292,187.3,292,112A88.1,88.1,0,0,0,204,24Zm0,56a32,32,0,1,1-32,32A32,32,0,0,1,204,80Z" fill="${themeColour('lureo-emphasis', 0.3)}"/>
  <!-- Main pin -->
  <path d="M${cx},16a88.1,88.1,0,0,0-88,88c0,75.3,80,132.17,83.41,134.55a8,8,0,0,0,9.18,0C208,236.17,288,179.3,288,104A88.1,88.1,0,0,0,${cx},16Zm0,56a32,32,0,1,1-32,32A32,32,0,0,1,${cx},72Z" fill="${escapeXml(color)}"/>
  ${labelSvg}
</svg>`;
}

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * Returns a google.maps.Icon descriptor for use with Marker or MarkerF.
 */
export function createLureoPinIcon(options: LureoPinOptions = {}): {
  url: string;
  scaledSize: google.maps.Size;
  anchor: google.maps.Point;
} {
  const width = options.width ?? LUREO_PIN_DEFAULT_WIDTH;
  const height = options.height ?? LUREO_PIN_DEFAULT_HEIGHT;
  const anchorX = width / 2;
  const anchorY = Math.round((LUREO_PIN_ANCHOR_Y / LUREO_PIN_DEFAULT_HEIGHT) * height);

  return {
    url: createLureoPinSvgUrl(options),
    scaledSize: new google.maps.Size(width, height),
    anchor: new google.maps.Point(anchorX, anchorY)
  };
}
